import { isFromBreakpoint, isUntilBreakpoint } from '~/breakpoints';
import { publish } from '~/event-bus';
import { classes, debounce, getFocusableElements, getTargets } from '~/helpers';
import { HTMLCustomElement } from '~/html-custom-element';

class SiteHeader extends HTMLCustomElement {
  connectedCallback() {
    new ResizeObserver(() => {
      document.body.style.setProperty('--header-height', `${this.getBoundingClientRect().height.toFixed(2)}px`);
    }).observe(this);

    this.subscribe('toggle-element-before-open', ({ id }) => {
      if (id === 'header-menu') {
        const parent = this.targets.menu.targets['submenu-target'].all.find(
          (target) => target.dataset.submenuTargetType === 'parent'
        );

        if (parent) {
          this.targets.menu.openSubmenu(parent.dataset.submenuTarget);
        }
      }
    });
  }
}

class HeaderMenu extends HTMLCustomElement {
  isFromMedium = isFromBreakpoint('medium');

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    if (this.targets['submenu-target']) {
      this.targets['submenu-target'].all.forEach((element) => {
        if (element.dataset.submenuTargetType === 'parent') {
          this.on('click', element, this.handleItemClickEvent.bind(this));
        }
      });

      /**
       * Desktop events.
       */
      [
        ['mouseenter', this.openSubmenu, 'target'],
        ['mouseleave', this.closeSubmenu, 'target'],
        ['focusin', this.openSubmenu, 'currentTarget'],
        ['focusout', this.closeSubmenu, 'currentTarget'],
      ].forEach(([name, handler, target]) => {
        this.on(name, this.targets['submenu-target'].all, (event) => {
          if (isFromBreakpoint('medium')) {
            handler.call(this, event[target].dataset.submenuTarget);
          }
        });
      });
    }

    if (this.targets.submenu) {
      this.targets.submenu.all.forEach((element) => {
        [...getFocusableElements(element)].forEach((focusable) => (focusable.tabIndex = -1));
      });
    }

    this.on('resize', window, debounce(this.handleWindowResize.bind(this)));
  }

  /**
   * Handles the menu item click event.
   * @param {MouseEvent} event
   */
  handleItemClickEvent(event) {
    const submenu = document.getElementById(event.currentTarget.dataset.submenuTarget);

    /**
     * Return if on desktop.
     */
    if (isFromBreakpoint('medium')) {
      return;
    }

    event.preventDefault();

    this.openSubmenu(submenu.id);
  }

  /**
   * Opens the submenu.
   * @param {String} id
   */
  openSubmenu(id) {
    this.targets['submenu-target'].all.forEach((element) => {
      if (element.dataset.submenuTarget === id) {
        element.classList.add(classes.open);

        if (element.dataset.submenuTargetType === 'parent') {
          const { link } = getTargets(element, this.tagName.toLowerCase());

          if (link) {
            link.ariaExpanded = true;
          }
        }

        return;
      }

      element.classList.remove(classes.open);
    });

    this.targets.submenu.all.forEach((element) => {
      if (element.id === id) {
        element.classList.add(classes.open);
        element.ariaHidden = false;

        [...getFocusableElements(element)].forEach((element) => {
          element.tabIndex = 0;
        });

        return;
      }

      element.classList.remove(classes.open);
    });
  }

  /**
   * Closes the submenu.
   * @param {String} id
   */
  closeSubmenu(id) {
    this.targets['submenu-target'].all.forEach((element) => {
      if (element.dataset.submenuTarget === id) {
        element.classList.remove(classes.open);

        if (element.dataset.submenuTargetType === 'parent') {
          const { link } = getTargets(element, this.tagName.toLowerCase());

          if (link) {
            link.ariaExpanded = false;
          }
        }
      }
    });

    this.targets.submenu.all.forEach((element) => {
      if (element.id === id) {
        element.classList.remove(classes.open);
        element.ariaHidden = true;

        [...getFocusableElements(element)].forEach((element) => {
          element.tabIndex = -1;
          element.blur();
        });
      }
    });
  }

  /**
   * Sets a ready state to signal that transitions can be safely applied.
   */
  handleWindowResize() {
    this.classList.remove(classes.ready);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add(classes.ready);
      });
    });

    if (!this.isFromMedium && isFromBreakpoint('medium')) {
      this.isFromMedium = true;

      /**
       * Close items when resizing from mobile to desktop.
       */
      this.targets.submenu.all.forEach((element) => {
        this.closeSubmenu(element.id);
      });
      return;
    }

    this.isFromMedium = isFromBreakpoint('medium');
  }
}

class SearchOverlay extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.controller = new AbortController();

    this.on('keyup', this.targets.input, debounce(this.search.bind(this)));
    this.on('input', this.targets.input, debounce(this.search.bind(this)));

    this.subscribe('toggle-element-opening', ({ id }) => {
      if (id === 'search-overlay' && this.targets.input) {
        requestAnimationFrame(() => {
          this.targets.input.focus();
          this.targets.input.setSelectionRange(this.targets.input.value.length, this.targets.input.value.length);
        });
      }
    });
  }

  /**
   * Runs the search query and updates.
   */
  search() {
    const formData = new FormData(this.targets.form);

    formData.append('section_id', this.getAttribute('section-id'));

    if (this.controller) {
      /**
       * Cancel the previous request.
       */
      this.controller.abort('Aborted pending search');
    }

    this.controller = new AbortController();

    try {
      fetch(`${theme.routes.search_url}?${new URLSearchParams(formData).toString()}`, {
        signal: this.controller.signal,
      })
        .then((response) => response.text())
        .then((response) => {
          publish('search-overlay-update', {
            sections: {
              [this.getAttribute('section-id')]: response,
            },
          });
        });
    } catch (error) {
      console.error(error);
    }
  }
}

if (!customElements.get('header-menu')) customElements.define('header-menu', HeaderMenu);
if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);
if (!customElements.get('search-overlay')) customElements.define('search-overlay', SearchOverlay);
